import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { extractorVendorMappingService } from 'companySettings/services';
import { IExtractorVendorMapping, ISaveExtractorVendorMappingPayload } from 'companySettings/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { GetManyResult } from 'core/types';

export const EXTRACTOR_VENDOR_MAPPINGS_QUERY_KEY = 'extractorVendorMappings';

interface IExtractorVendorMappingActionsProps {
  position?: NotificationPosition;
  selectionCount?: number;
  // Set by a caller that refreshes the list itself, after it is done with its own notification —
  // see the Edit drawer, whose remove lives inside the very row the refetch replaces.
  hasDeferredListRefresh?: boolean;
}

// One unpaginated request holds the whole company-scoped list, which the grid then filters, sorts
// and quick-searches in the browser. Deliberately NOT the paginated `useInfiniteQuery` pattern: the
// endpoint's `updated_at desc` order has no id tiebreaker, so a page walk could repeat or skip rows
// that share a second (see the service).
export const useGetExtractorVendorMappings = (): GetManyResult<IExtractorVendorMapping> => {
  const { data, isError, isLoading } = useQuery(
    [EXTRACTOR_VENDOR_MAPPINGS_QUERY_KEY],
    ({ signal }) => extractorVendorMappingService.getExtractorVendorMappings(signal)
  );

  return { records: data?.records ?? [], count: data?.count, isError, isLoading };
};

export const useExtractorVendorMappingActions = ({
  position = NotificationPosition.FORM,
  selectionCount = 1,
  hasDeferredListRefresh = false,
}: IExtractorVendorMappingActionsProps = {}) => ({
  ...useSaveExtractorVendorMapping(position),
  ...useRemoveExtractorVendorMappings(selectionCount, position, hasDeferredListRefresh),
});

// One endpoint covers both modes, so the payload's `id` — the create-vs-edit discriminator on the
// wire — also picks the wording. `useHandleErrors` hands the mutation variables to every message
// callback.
// Exported on its own for the drawer, which the grid mounts once per visible row: taking the
// composed hook there would register the remove mutation too, and with it the `invalidateQueries`
// the drawer deliberately defers until it has closed.
export const useSaveExtractorVendorMapping = (
  position: NotificationPosition = NotificationPosition.FORM
) => {
  const { errors } = useHandleErrors({
    success: ({ id }: ISaveExtractorVendorMappingPayload) => ({
      message: id
        ? t('messageExtractorVendorMappingUpdateSuccess')
        : t('messageExtractorVendorMappingCreateSuccess'),
    }),
    loading: ({ id }: ISaveExtractorVendorMappingPayload) => ({
      message: id
        ? t('messageExtractorVendorMappingUpdateInProgress')
        : t('messageExtractorVendorMappingCreateInProgress'),
    }),
    // The 422 body carries only a sentence (`errors` is always empty), so the notification is the
    // only place a server rejection — e.g. a duplicate extractor id — can be read. Keep it up.
    error: ({ id }: ISaveExtractorVendorMappingPayload) => ({
      title: id
        ? t('errorTitleExtractorVendorMappingUpdate')
        : t('errorTitleExtractorVendorMappingCreate'),
      disableAutoClose: true,
    }),
    position,
    // NO `invalidateQueries` here on purpose. The Edit drawer is rendered by the row's own actions
    // cell, and a refetch replaces that row's node — which would unmount the drawer, and the FORM
    // notification inside it, the moment the response landed. The drawer therefore closes first and
    // invalidates afterwards; see `UpsertExtractorVendorMapping`.
  });

  const { mutateAsync } = useMutation(extractorVendorMappingService.saveExtractorVendorMapping, {
    ...errors,
  });

  return { saveExtractorVendorMapping: mutateAsync };
};

// The three remove messages only exist as `_one` / `_other`, so `count` is mandatory — without it
// i18next finds no exact key and echoes the key name back into the notification.
const useRemoveExtractorVendorMappings = (
  selectionCount: number,
  position: NotificationPosition,
  hasDeferredListRefresh: boolean
) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageExtractorVendorMappingRemoveSuccess', { count: selectionCount }),
    },
    loading: {
      message: t('messageExtractorVendorMappingRemoveInProgress', { count: selectionCount }),
    },
    error: { title: t('errorTitleExtractorVendorMappingRemove') },
    position,
    // Safe from the grid, unlike on the write: that notification is at TOP, outside the row, and a
    // removed row's own trash button has nothing left to render after the refetch anyway. The Edit
    // drawer is the exception — its notification sits INSIDE the row — so it refreshes the list
    // itself once it has closed.
    ...(!hasDeferredListRefresh && { invalidateQueries: [EXTRACTOR_VENDOR_MAPPINGS_QUERY_KEY] }),
  });

  const { mutateAsync } = useMutation(extractorVendorMappingService.removeExtractorVendorMappings, {
    ...errors,
  });

  return { removeExtractorVendorMappings: mutateAsync };
};
